import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FrameWithStickyTitle } from 'components/BrandSlides/BrandSlides';
import { getShuffledIcons } from 'utils/iconUtils';
import { ensureArray, safeMap, ensureString } from 'utils/safeArray';

export default function CompanyContext({ title, note, kpiHeaders, kpiRows, accent_color }) {
  const safeKpiRows = ensureArray(kpiRows);
  const safeKpiHeaders = ensureArray(kpiHeaders);
  const derivedColumnCount = safeKpiHeaders.length || (safeKpiRows[0] ? ensureArray(safeKpiRows[0]).length : 2);
  const numCols = derivedColumnCount || 2;
  // Mobile: stack vertically, Desktop: 1/3 for first column, 2/3 for second column
  const gridColsClass = numCols === 2
    ? 'grid-cols-1 md:grid-cols-[1fr_2fr]'
    : numCols === 3
    ? 'grid-cols-1 md:grid-cols-[1fr_2fr_2fr]'
    : 'grid-cols-1 md:grid-cols-[1fr_2fr_2fr_2fr]';

  // Shuffle icons once and memoize
  const shuffledIcons = useMemo(() => getShuffledIcons(), []);

  return (
    <FrameWithStickyTitle title={title} accentColor={accent_color}>
      <div className="w-full h-full flex flex-col text-left pt-4">
        {note && (
          <div
            className="w-full text-left border rounded-lg bg-slate-50/70 px-4"
            style={{ borderColor: (accent_color || '#cc0000') }}
          >
            <p className="text-base text-slate-600 leading-relaxed text-left">{ensureString(note)}</p>
          </div>
        )}
        <div className="w-full flex-1 flex flex-col text-left mt-2">
          {/* Grid Body */}
          <div className="space-y-2.5 text-left">
            {safeMap(safeKpiRows, (row, i) => {
              const IconComponent = shuffledIcons[i];
              const safeRowCells = ensureArray(row);
              return (
                <div key={i} className={`grid ${gridColsClass} gap-x-3 md:gap-x-6 gap-y-2 py-2 hover:bg-slate-50 transition-colors rounded-xl`}>
                  {/* First column - highlighted with icon inside */}
                  {safeMap(safeRowCells, (cell, j) => (
                    <div
                      key={j}
                      className={`text-base md:text-lg leading-relaxed ${
                        j === 0
                          ? 'font-semibold text-slate-800 bg-slate-50/80 rounded-lg flex items-center gap-2 md:gap-4 pr-2 md:pr-4'
                          : 'text-slate-700 flex items-center'
                      }`}
                    >
                      {j === 0 && IconComponent && (
                        <div className="flex items-center justify-center pl-2 md:pl-4">
                          <IconComponent
                            className="h-6 w-6 md:h-9 md:w-9 flex-shrink-0"
                            style={{ color: accent_color || '#cc0000' }}
                          />
                        </div>
                      )}
                      <div className={j === 0 ? 'py-3 md:py-4 pr-2' : ''}>
                        {ensureString(cell)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </FrameWithStickyTitle>
  );
}

CompanyContext.propTypes = {
  title: PropTypes.string,
  note: PropTypes.string,
  kpiTitle: PropTypes.string,
  kpiHeaders: PropTypes.array,
  kpiRows: PropTypes.array,
  accent_color: PropTypes.string,
};
